import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Account, ApplicantApplication, ApplicantHome, Login, Register } from '../containers'
import { Home } from '../containers/Home/Home'
import {
    capturePendingInviteToken,
    clearPendingInviteToken,
    LocalStorage,
} from '../helpers'
import { axiosInstance } from '../helpers/axios'
import { AppRoutes } from '../helpers/Routes'
import { useAppDispatch } from '../hooks'
import { RootState } from '../store'
import {
    claimPendingInvite,
    fetchLeasingContext,
    setRootUser,
} from '../store/User/action'
import { Layout } from './Layout'
import { SelectArea } from '../containers/MoveInInspection/SelectArea'
import { Issues } from '../containers/MoveInInspection/Issues'
import { ForgotPassword } from '../containers/Login/ForgotPassword/ForgotPassword'
import { CreateServiceRequest } from '../containers/ServiceRequest/CreateServiceRequest'
import { Reports } from '../containers/Reports/Reports'
import { LeaseContext, useLeaseState } from '../ctx/lease'
import { ManagePayments } from '../containers/ManagePayments/ManagePayments'
import { StripeCheckout } from '../components/Payments/StripeCheckout'
import { fetchLeases } from '../store/Lease/action'
import { fetchBills } from '../store/Billing/action'
import { Maintenance } from '../containers/Maintenance/Maintenance'
import { MoveIn } from '../containers/MoveIn/MoveIn'
import { BillPay } from '../containers/ManagePayments/BillPay'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material'
import { DamageList } from '../containers/Damage/DamageList'
import { MoveOut } from '../containers/MoveOut/MoveOut'
import { Documents } from '../containers/Documents/Documents'
import { Inspections } from '../containers/Inspections/Inspections'
import { MoveInInspectionDetail } from '../containers/Inspections/MoveInInspectionDetail'
import { MoveOutInspectionDetail } from '../containers/Inspections/MoveOutInspectionDetail'
import { PropertyInspectionDetail } from '../containers/Inspections/PropertyInspectionDetail'
import { Invoices } from '../containers/Invoices/Invoices'
import { MessagesInbox } from '../containers/Messages/MessagesInbox'
import { MessageDetail } from '../containers/Messages/MessageDetail'
import { Events } from '../containers/Events/Events'
import { EventDetail } from '../containers/Events/EventDetail'
import { LeasingContext, LeasingMode } from '../types/leasingContext'

const inviteClaimErrorMessage = (err: unknown) => {
    const axiosError = err as { response?: { data?: { error?: string } } }
    return (
        axiosError.response?.data?.error ||
        'This invitation is for a different account. Sign out and sign in with that email.'
    )
}

export const NavigationController = () => {
    const [isInitializing, setIsInitializing] = useState(true)
    const [inviteClaimError, setInviteClaimError] = useState<string | null>(null)
    const claimedInviteRef = useRef<string | null>(null)
    const rootUser = useSelector((state: RootState) => state.user.rootUser)
    const leasingContext = useSelector(
        (state: RootState) => state.user.leasingContext,
    )
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const location = useLocation()
    const navigate = useNavigate()

    const token = localStorage.getItem(LocalStorage.EZTURN_TENANT_TOKEN)
    const unauthenticated = rootUser === undefined
    const tokenExists = token !== null
    const showSplash = unauthenticated && tokenExists

    const dispatch = useAppDispatch()

    const portalMode: LeasingMode = leasingContext?.mode || 'none'
    const isApplicant = portalMode === 'applicant'

    const claimInvite = async (
        inviteToken: string,
    ): Promise<LeasingContext | null> => {
        if (!inviteToken || claimedInviteRef.current === inviteToken) {
            return null
        }
        claimedInviteRef.current = inviteToken
        try {
            const context = await dispatch(claimPendingInvite(inviteToken))
            clearPendingInviteToken()
            return context
        } catch (err) {
            const axiosError = err as { response?: { status?: number } }
            const status = axiosError.response?.status
            if (status === 403) {
                setInviteClaimError(inviteClaimErrorMessage(err))
            } else {
                clearPendingInviteToken()
            }
            return null
        }
    }

    useEffect(() => {
        const initializeApp = async () => {
            const inviteToken = capturePendingInviteToken()
            if (token) {
                try {
                    axiosInstance.defaults.headers.common[
                        'Authorization'
                    ] = `token ${token}`
                    const res = await axiosInstance.get(`user/get_root_user/`)
                    dispatch(setRootUser(res.data))
                    const claimed = inviteToken
                        ? await claimInvite(inviteToken)
                        : null
                    const context =
                        claimed ?? (await dispatch(fetchLeasingContext()))
                    if (context?.mode !== 'applicant') {
                        dispatch(fetchLeases())
                        dispatch(fetchBills())
                    }
                    if (claimed) {
                        navigate(AppRoutes.home, { replace: true })
                    }
                } catch (error) {
                    dispatch(setRootUser(undefined))
                    localStorage.removeItem(LocalStorage.EZTURN_TENANT_TOKEN)
                }
            }
            setIsInitializing(false)
        }

        initializeApp()
    }, [dispatch, token])

    useEffect(() => {
        if (isInitializing || !rootUser) {
            return
        }
        const inviteToken = capturePendingInviteToken(location.search)
        if (!inviteToken || claimedInviteRef.current === inviteToken) {
            return
        }
        claimInvite(inviteToken).then((claimed) => {
            if (claimed) {
                navigate(AppRoutes.home, { replace: true })
            } else {
                dispatch(fetchLeasingContext())
            }
        })
    }, [rootUser, isInitializing, location.search])

    useEffect(() => {
        if (rootUser && leasingContext === undefined && !capturePendingInviteToken()) {
            dispatch(fetchLeasingContext())
        }
    }, [rootUser, leasingContext, dispatch])

    useEffect(() => {
        if (rootUser && leasingContext && !isApplicant) {
            dispatch(fetchLeases())
            dispatch(fetchBills())
        }
    }, [rootUser, leasingContext, isApplicant, dispatch])

    const residentRoutes = [
        { path: AppRoutes.home, component: <Home /> },
        { path: AppRoutes.selectArea, component: <SelectArea /> },
        { path: AppRoutes.issues, component: <Issues /> },
        { path: AppRoutes.createServiceRequest, component: <CreateServiceRequest /> },
        { path: AppRoutes.reports, component: <Reports /> },
        { path: AppRoutes.managePayments, component: <ManagePayments /> },
        { path: AppRoutes.stripeCheckout, component: <StripeCheckout /> },
        { path: AppRoutes.maintenance, component: <Maintenance /> },
        { path: AppRoutes.moveIn, component: <MoveIn /> },
        { path: AppRoutes.billPay, component: <BillPay /> },
        { path: AppRoutes.damages, component: <DamageList /> },
        { path: AppRoutes.moveOut, component: <MoveOut /> },
        { path: AppRoutes.documents, component: <Documents /> },
        { path: AppRoutes.inspections, component: <Inspections /> },
        { path: AppRoutes.invoices, component: <Invoices /> },
        { path: AppRoutes.inspectionMoveIn, component: <MoveInInspectionDetail /> },
        { path: AppRoutes.inspectionMoveOut, component: <MoveOutInspectionDetail /> },
        { path: AppRoutes.inspectionPropertyDetail, component: <PropertyInspectionDetail /> },
        { path: AppRoutes.messages, component: <MessagesInbox /> },
        { path: AppRoutes.messageDetail, component: <MessageDetail /> },
        { path: AppRoutes.events, component: <Events /> },
        { path: AppRoutes.eventDetail, component: <EventDetail /> },
        { path: AppRoutes.account, component: <Account /> },
    ]

    const applicantRoutes = [
        { path: AppRoutes.home, component: <ApplicantHome /> },
        { path: AppRoutes.applicant, component: <ApplicantApplication /> },
        { path: AppRoutes.documents, component: <Documents /> },
        { path: AppRoutes.messages, component: <MessagesInbox /> },
        { path: AppRoutes.messageDetail, component: <MessageDetail /> },
        { path: AppRoutes.account, component: <Account /> },
    ]

    const authRoutes = [
        { path: AppRoutes.login, component: <Login /> },
        { path: AppRoutes.register, component: <Register /> },
        { path: AppRoutes.resetPassword, component: <ForgotPassword /> },
    ]

    let redirectRoute = AppRoutes.login
    const leaseState = useLeaseState()

    let applicationRoutes: typeof authRoutes = authRoutes
    if (rootUser) {
        applicationRoutes = isApplicant ? applicantRoutes : residentRoutes
        redirectRoute = AppRoutes.home
    }

    const handleSignOutForInvite = () => {
        const inviteToken = localStorage.getItem(LocalStorage.PENDING_INVITE_TOKEN)
        dispatch(setRootUser(undefined))
        localStorage.removeItem(LocalStorage.EZTURN_TENANT_TOKEN)
        delete axiosInstance.defaults.headers.common['Authorization']
        setInviteClaimError(null)
        claimedInviteRef.current = null
        navigate(
            inviteToken
                ? `${AppRoutes.register}?invite=${inviteToken}`
                : AppRoutes.login,
        )
    }

    if (isInitializing || showSplash) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <>
            <Layout
                loggedIn={rootUser !== undefined}
                selectedLease={selectedLease}
                mode={portalMode}
            >
                <LeaseContext.Provider value={leaseState}>
                    <Routes>
                        {applicationRoutes.map((r) => (
                            <Route
                                element={r.component}
                                path={r.path}
                                key={`APP_ROUTE_${r.path}`}
                            />
                        ))}
                        <Route path="*" element={<Navigate to={redirectRoute} />} />
                    </Routes>
                </LeaseContext.Provider>
            </Layout>
            <Dialog
                open={Boolean(inviteClaimError)}
                onClose={() => setInviteClaimError(null)}
            >
                <DialogTitle>Wrong account</DialogTitle>
                <DialogContent>
                    <Typography>{inviteClaimError}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteClaimError(null)}>
                        Stay signed in
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSignOutForInvite}
                    >
                        Sign out
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
